<template>
  <v-dialog :value="modalVisible" eager fullscreen hide-overlay transition="dialog-bottom-transition">
    <v-toolbar dark color="white">
      <v-spacer></v-spacer>
      <v-toolbar-items>
        <v-btn dark text @click="onCloseModalButtonClicked()">
          <v-icon class="text--secondary">mdi-close</v-icon>
        </v-btn>
      </v-toolbar-items>
    </v-toolbar>

    <v-card tile data-cy="confirmLocationModal">
      <div id="mapButtonContainer">
        <v-btn v-if="!islistMode" :loading="geolocating" color="white" fab @click="geolocate()">
          <v-icon color="primary">mdi-near-me</v-icon>
        </v-btn>

        <v-btn color="white" fab class="mt-3" @click="toggleList()">
          <v-icon v-if="islistMode" color="primary">mdi-map</v-icon>
          <v-icon v-else color="primary">mdi-format-list-bulleted-square</v-icon>
        </v-btn>
      </div>

      <v-row align="center" justify="center" class="px-4 pb-4">
        <v-col cols="12">
          <div class="body font-weight-bold">Almost there... Please confirm desired location</div>
          <div class="font-weight-light body-2">This offer only works at the location you choose</div>
        </v-col>
      </v-row>

      <v-list v-if="islistMode" :disabled="confirmLocationLoading" class="pt-0 px-3">
        <v-subheader class="font-weight-bold">Locations</v-subheader>
        <v-list-item-group v-model="selectedAddressId" @change="onOfferLocationSelected">
          <v-list-item
            v-for="address in addresses"
            :key="address.address_id"
            :ref="`location-${address.address_id}`"
            :value="address.address_id"
            two-line
            class="location-list-item"
          >
            <template #default="{ active }">
              <v-list-item-content class="pb-0 pt-0">
                <v-list-item-title :class="active ? '' : 'font-weight-light'">{{ address.address }}, {{ address.city }}</v-list-item-title>
                <v-list-item-subtitle>
                  <v-row class="text-center">
                    <v-col v-if="address.vouchers_left <= 0">
                      <v-icon dense small class="mt-n1">mdi-clipboard-list-outline</v-icon>
                      Wait List
                    </v-col>
                    <v-col v-else>
                      <v-icon dense small class="mt-n1">mdi-ticket-confirmation-outline</v-icon>
                      {{ address.vouchers_left | manyNumberPretty }} Available
                    </v-col>
                    <v-col>
                      <v-icon dense small class="pr-1">mdi-car</v-icon>
                      {{ address.distance | roundedFull }} mi
                    </v-col>
                    <v-col>
                      <v-icon dense small class="pr-1 mt-n1">mdi-clock-outline</v-icon>
                      {{ new Date() | shortDate }}
                    </v-col>
                  </v-row>
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                <v-icon v-if="active" color="primary">mdi-check-circle</v-icon>
              </v-list-item-action>
            </template>
          </v-list-item>
        </v-list-item-group>
      </v-list>

      <v-container fluid :style="{ display: listMode !== 'map' ? 'none' : 'block' }" class="mapContainer pa-0">
        <v-layout fill-height>
          <div style="height: 100%; width: 100%">
            <div id="googleMap" ref="googleMap">
              <app-spinner v-if="loading" class="mt-10" />
            </div>
          </div>
        </v-layout>
      </v-container>

      <v-footer app :fixed="true" :elevation="6" class="white">
        <v-row class="py-2">
          <v-col cols="12">
            <v-btn :disabled="loading || !selectedAddressId" :loading="confirmLocationLoading" color="primary" rounded block @click="onConfirmLocationClicked">
              {{ actionButtonText }}
            </v-btn>
          </v-col>
        </v-row>
      </v-footer>
    </v-card>
  </v-dialog>
</template>

<script>
import Vue from 'vue'

import { GoogleMapService } from '@/services'

export default Vue.extend({
  name: 'ConfirmLocationModal',

  props: {
    modalVisible: {
      type: Boolean,
      required: true,
    },
    confirmMode: {
      type: Boolean,
      default: true,
    },
    confirmLocationLoading: {
      type: Boolean,
      default: false,
    },
    addresses: {
      type: Array,
      required: true,
    },
    currentAddress: {
      type: Object,
      required: true,
    },
  },

  data: () => ({
    loading: true,
    geolocating: false,
    currentLocationMarker: null,
    selectedAddressId: null,
    listMode: 'map',
    map: null,
    markers: [],
    infoWindow: null,
  }),

  computed: {
    actionButtonText: function () {
      return this.confirmMode ? 'Confirm Location' : 'Choose Location'
    },
    islistMode: function () {
      return this.listMode === 'list'
    },
  },

  watch: {
    modalVisible() {
      if (!this.modalVisible) return

      this.listMode = 'map'

      const bounds = new window.google.maps.LatLngBounds()

      const selectedAddressListPosition = this.addresses.findIndex((address) => {
        return address.address_id === this.currentAddress.address_id
      })

      const nearbyAddresses = this.addresses.slice(
        Math.max(0, selectedAddressListPosition - 1),
        Math.min(this.addresses.length, selectedAddressListPosition + 1)
      )

      nearbyAddresses.forEach((address) => {
        bounds.extend(new window.google.maps.LatLng(address.latitude, address.longitude))
      })

      this.map.fitBounds(bounds)
      this.map.setZoom(10)
    },
  },

  mounted: function () {
    Vue.nextTick(() => {
      GoogleMapService.init().then(() => {
        this.map = new window.google.maps.Map(this.$refs.googleMap, {
          maxZoom: 13,
          controlSize: 25,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          gestureHandling: 'greedy',
          zoomControlOptions: {
            position: window.google.maps.ControlPosition.RIGHT_TOP,
          },
        })

        this.infoWindow = new window.google.maps.InfoWindow()
        this.buildAddressMarkers()
      })
    })
  },

  methods: {
    buildAddressMarkers() {
      this.addresses.forEach((location) => {
        const marker = GoogleMapService.buildMarker(location.latitude, location.longitude, {
          addressId: location.address_id,
          address: location.address,
          vouchersLeft: Math.max(0, location.vouchers_left),
          city: location.city,
          label: Math.max(0, location.vouchers_left).toString(),
          icon: {
            url: 'https://storage.googleapis.com/localfluence-library-production/map-loc.png',
            labelOrigin: new window.google.maps.Point(38, 16),
          },
        })
        marker.setMap(this.map)
        marker.addListener('click', () => this.onOfferLocationSelected(location.address_id))

        this.markers.push(marker)

        if (this.currentAddress.address_id === location.address_id) {
          this.onOfferLocationSelected(location.address_id)
        }
      })

      this.loading = false
    },
    geolocate() {
      if (this.geolocating) return

      if (!navigator.geolocation) {
        this.$toast.error('Your browser does not support Geolocation')
        return
      }

      this.geolocating = true

      if (this.currentLocationMarker) {
        this.currentLocationMarker.setMap(null)
      }

      navigator.geolocation.getCurrentPosition(this.onGeoLocationSuccess, this.onGeolocateError, { maximumAge: 600000, timeout: 30000 })
    },
    onGeoLocationSuccess(result) {
      const { latitude, longitude } = result.coords

      this.currentLocationMarker = new window.google.maps.Circle({
        scale: 1,
        radius: 300,
        strokeWeight: 3,
        fillOpacity: 0.3,
        strokeOpacity: 0.7,
        fillColor: '#82B1FF',
        strokeColor: '#0D47A1',
        map: this.map,
        center: { lat: latitude, lng: longitude },
      })

      this.map.setZoom(13)
      this.map.panTo(new window.google.maps.LatLng(latitude, longitude))

      this.geolocating = false
    },
    onGeolocateError(error) {
      this.geolocating = false

      switch (error.code) {
        case 1:
          this.$toast.warning('Please allow access to your location')
          break

        case 2:
          this.$toast.error('Location not available')
          break

        default:
          this.$toast.error('Unexpected error, please try again')
      }
    },
    onOfferLocationSelected(addressId) {
      try {
        this.selectedAddressId = addressId

        const marker = this.markers.find((marker) => marker.addressId === addressId)
        const bounds = new window.google.maps.LatLngBounds().extend(marker.position)
        this.map.fitBounds(bounds)

        this.infoWindow.setOptions({
          maxWidth: 350,
          pixelOffset: new window.google.maps.Size(0, 43),
          content: `
          <div style="display: flex;">
            <div style="display: flex; border-right: 1px solid #fff; padding-left: 5px; align-items: center;">
              <img src="https://storage.googleapis.com/localfluence-library-production/ticket-1614821495.svg" width="18" style="fill: #fff;"/>
              <h2 style="font-weight: 300; padding-left: 3px; padding-right: 3px;">${marker.vouchersLeft}</h2>
            </div>
            <div style="display: flex; flex-direction: column; padding: 5px; margin-right: 3px;">
              <h3>${marker.address}</h3>
              <span>${marker.city}</span>
            </div>
          </div>
          `,
        })

        this.infoWindow.open(this.map, marker)
      } catch (error) {
        this.$toast.error(error)
      }
    },
    onCloseModalButtonClicked() {
      if (this.confirmLocationLoading) return

      this.$emit('confirm-location-modal-close')

      this.onOfferLocationSelected(this.currentAddress.address_id)
    },
    onConfirmLocationClicked() {
      this.$emit('confirm-location-modal-location-selected', this.selectedAddressId)
    },
    toggleList() {
      this.listMode = this.islistMode ? 'map' : 'list'
    },
  },
})
</script>

<style scoped>
#mapButtonContainer {
  right: 10px;
  bottom: 120px;
  z-index: 100;
  display: flex;
  position: fixed;
  flex-direction: column;
}

.list-toggle-button {
  bottom: 100px !important;
}

.v-btn:before {
  background-color: #fff;
}

.location-list-item {
  border-top: 1px solid #e0e0e0;
}

#googleMap {
  height: 100%;
}

.mapContainer {
  height: 70vh;
  background: #e0e0e0;
}

#googleMap >>> .gm-style .gm-style-iw {
  color: #fff;
  padding: 0 !important;
  border: 2px solid white;
  border-radius: 24px !important;
  background-color: #ed1e56 !important;
}

#googleMap >>> .gm-style .gm-style-iw-d {
  overflow: auto !important;
}

#googleMap >>> .gm-style-iw > button {
  display: none !important;
}
</style>
